/**
 * @fileoverview FirmContactNotesFeed — thin container wiring the shared
 * `NotesFeedView` to the **firm-contact** notes endpoints.
 *
 * Org-level analogue of the job-contact `ContactNotesFeed`. Owns the
 * Convex data (`api.contacts.firmNotes.*` + `firmNotesSummary.*`) and the
 * summarise busy-state; all presentation lives in the shared
 * `src/components/contacts/notes/` components. Firm contacts have no
 * job/agent-source context, so the "Chat" affordance is omitted.
 */

import { useCallback, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { NotesFeedView } from "@/components/contacts/notes/NotesFeedView";
import type { FeedNote } from "@/components/contacts/notes/NoteRow";

interface NotesSummaryFromContact {
  text: string;
  generatedAt: number;
  generatedBy: Id<"users">;
  noteCount: number;
  notesHash: string;
  model: string;
}

interface FirmContactNotesFeedProps {
  firmContactId: Id<"firmContacts">;
  /** Read-only when the firm contact is archived. */
  readOnly?: boolean;
  /** The current authenticated user (used to decide which rows are editable). */
  currentUserId?: Id<"users">;
  /** Cached AI summary read from the parent firm-contact query. */
  notesSummary?: NotesSummaryFromContact;
}

export function FirmContactNotesFeed({
  firmContactId,
  readOnly = false,
  currentUserId,
  notesSummary,
}: FirmContactNotesFeedProps) {
  const { data: notes, isLoading } = useQuery({
    ...convexQuery(api.contacts.firmNotes.listFirmContactNotes, {
      contactId: firmContactId,
    }),
  });

  const createNote = useConvexMutation(
    api.contacts.firmNotes.createFirmContactNote,
  );
  const updateNote = useConvexMutation(
    api.contacts.firmNotes.updateFirmContactNote,
  );
  const deleteNote = useConvexMutation(
    api.contacts.firmNotes.deleteFirmContactNote,
  );
  const summariseAction = useConvexAction(
    api.contacts.firmNotesSummary.summariseFirmContactNotes,
  );

  const { mutateAsync: doSummarise } = useMutation({
    mutationFn: summariseAction,
  });
  const [isSummarising, setIsSummarising] = useState(false);

  // Firm notes have no parent count to read from, so the live count is
  // derived straight from the feed query.
  const notesCount = notes?.length ?? 0;

  const handlePost = useCallback(
    async (content: string) => {
      await createNote({ contactId: firmContactId, content });
    },
    [firmContactId, createNote],
  );

  const handleSaveNote = useCallback(
    async (noteId: string, content: string) => {
      await updateNote({ noteId: noteId as Id<"firmContactNotes">, content });
    },
    [updateNote],
  );

  const handleDeleteNote = useCallback(
    async (noteId: string) => {
      await deleteNote({ noteId: noteId as Id<"firmContactNotes"> });
    },
    [deleteNote],
  );

  const handleGenerateSummary = useCallback(async () => {
    if (isSummarising) return;
    if (notesCount === 0) {
      toast.info("Post a note first, then I can summarise the feed.");
      return;
    }
    setIsSummarising(true);
    try {
      const result = await doSummarise({ contactId: firmContactId });
      toast.success(result.cached ? "Summary up to date" : "Summary ready");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not generate the summary";
      toast.error(message);
    } finally {
      setIsSummarising(false);
    }
  }, [firmContactId, notesCount, isSummarising, doSummarise]);

  const handleRefreshSummary = useCallback(async () => {
    if (isSummarising) return;
    setIsSummarising(true);
    try {
      await doSummarise({ contactId: firmContactId, force: true });
      toast.success("Summary refreshed");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not refresh the summary";
      toast.error(message);
    } finally {
      setIsSummarising(false);
    }
  }, [firmContactId, isSummarising, doSummarise]);

  const notesList: Array<FeedNote> = useMemo(
    () =>
      (notes ?? []).map((n) => ({
        _id: n._id,
        content: n.content,
        createdBy: n.createdBy,
        createdAt: n.createdAt,
        updatedAt: n.updatedAt,
        authorDisplayName: n.authorDisplayName,
        lastEditedByName: n.lastEditedByName ?? undefined,
      })),
    [notes],
  );

  return (
    <NotesFeedView
      notes={notesList}
      notesCount={notesCount}
      isLoading={isLoading}
      readOnly={readOnly}
      currentUserId={currentUserId}
      summary={notesSummary ?? null}
      isSummarising={isSummarising}
      onPost={handlePost}
      onSaveNote={handleSaveNote}
      onDeleteNote={handleDeleteNote}
      onGenerateSummary={() => void handleGenerateSummary()}
      onRefreshSummary={() => void handleRefreshSummary()}
    />
  );
}
